import { Box, useColorModeValue } from "@chakra-ui/react";
import { useToken } from "@chakra-ui/system";
import { useChartColors } from "utils";

const VIEWBOX_SIZE = 100;
const CENTER = VIEWBOX_SIZE / 2;

export type GaugeProps = {
  value: number;
  max?: number;
  color?: string;
  trackColor?: string;
  thickness?: number;
  fontSize?: number;
  formatValue?: (value: number) => string;
};

const Gauge = ({
  value,
  max = 100,
  color = "teal.500",
  trackColor,
  thickness = 12,
  fontSize = 26,
  formatValue = (v) => String(Math.round(v)),
}: GaugeProps) => {
  const defaultTrackColor = useColorModeValue("gray.100", "gray.600");
  const { labelColor } = useChartColors();

  const [arcColor, resolvedTrackColor] = useToken("colors", [
    color,
    trackColor ?? defaultTrackColor,
  ]);

  const radius = (VIEWBOX_SIZE - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;

  return (
    <Box width="100%" height="100%">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ fontFamily: "var(--chakra-fonts-body)", display: "block" }}
      >
        <circle
          cx={CENTER}
          cy={CENTER}
          r={radius}
          fill="none"
          stroke={resolvedTrackColor}
          strokeWidth={thickness}
        />

        <circle
          cx={CENTER}
          cy={CENTER}
          r={radius}
          fill="none"
          stroke={arcColor}
          strokeWidth={thickness}
          strokeDasharray={`${ratio * circumference} ${circumference}`}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
          style={{ transition: "stroke-dasharray 600ms ease-out" }}
        />

        <text
          x={CENTER}
          y={CENTER}
          fill={labelColor}
          fontSize={fontSize}
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {formatValue(value)}
        </text>
      </svg>
    </Box>
  );
};

export { Gauge };
